export type PublicUserRole =
  | 'FAN'
  | 'PLAYER'
  | 'TEAM_MANAGER'
  | 'REFEREE'
  | 'VENUE_OWNER'
  | 'TOURNAMENT_ORGANIZER'
  | 'FEDERATION'
  | 'MAIN_ADMIN';

export interface SessionUser {
  id: string;
  email: string;
  phone: string | null;
  role: PublicUserRole;
  status: 'ACTIVE';
  createdAt: string;
  updatedAt: string;
}

export interface BrowserSession {
  accessToken: string;
  user: SessionUser;
  portal: 'admin' | 'owner' | 'public';
}

const apiRoot = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
const browserAuthUrl = (path: string) => `${apiRoot}/api/auth/browser${path}`;

let accessToken: string | null = null;
let refreshRequest: Promise<BrowserSession | null> | null = null;

async function parseError(response: Response, fallback: string) {
  const body = (await response.json().catch(() => ({}))) as {
    message?: string;
    error?: string;
  };
  return body.message ?? body.error ?? fallback;
}

async function request(path: string, body?: unknown, token?: string | null) {
  return fetch(browserAuthUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
}

export async function restoreSession(): Promise<BrowserSession | null> {
  refreshRequest ??= request('/refresh')
    .then(async (response) => {
      if (!response.ok) {
        accessToken = null;
        return null;
      }
      const session = (await response.json()) as BrowserSession;
      accessToken = session.accessToken;
      return session;
    })
    .catch(() => {
      accessToken = null;
      return null;
    })
    .finally(() => {
      refreshRequest = null;
    });
  return refreshRequest;
}

export async function loginWithGoogle(idToken: string) {
  const response = await request('/google/login', { idToken });
  if (!response.ok) {
    throw new Error(await parseError(response, 'Google sign-in failed'));
  }
  const session = (await response.json()) as BrowserSession;
  accessToken = session.accessToken;
  return session;
}

export async function registerWithGoogle(idToken: string) {
  const response = await request('/google/register', { idToken });
  if (!response.ok) {
    throw new Error(
      await parseError(response, 'Google account creation failed'),
    );
  }
  const session = (await response.json()) as BrowserSession;
  accessToken = session.accessToken;
  return session;
}

export async function logoutSession() {
  const token = accessToken;
  accessToken = null;
  await request('/logout', undefined, token).catch(() => undefined);
}
